import { IconButton } from '@mui/material';
import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import ArchiveIcon from '@mui/icons-material/Archive';
import MenuItem from "@mui/material/MenuItem";
import MenuNavBar from "./MenuNavBar";
import { Grid, Typography } from "@mui/material";
import { ThemeContext } from "../../contexts/ThemeContext";
import { useContext } from "react";
import { styled } from "@mui/material/styles";
import { PagesList, paypalDonateLink } from "../../config";
import { LayoutContext } from "../../contexts/LayoutContext/LayoutContext";
import { Link as RouterLink } from "react-router-dom";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TheatersIcon from '@mui/icons-material/Theaters';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AlbumIcon from '@mui/icons-material/Album';
import InfoIcon from '@mui/icons-material/Info';
import HomeIcon from '@mui/icons-material/Home';

const Link = styled(RouterLink)(({ theme }) => ({
    textDecoration: "none",
    color: theme.palette.text.primary,
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
}));

const StyledAnchor = styled('a')(({ theme }) => ({
    textDecoration: 'none',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px', // Added padding for better click area
}));

const commonTypographyStyle = {
    fontSize: '1.1rem', // Increased from 1rem
    fontWeight: '500', // Made slightly bolder
    marginLeft: '6px', // Added spacing between icon and text
};

export const MainMenuOptions = ({ isColumnMenu, handleClose }) => {
    const { theme } = useContext(ThemeContext);

    const menuOptions = [
        { route: PagesList.homepage.route, name: 'Home', icon: <HomeIcon /> },
        { route: PagesList.aboutUs.route, name: 'About', icon: <InfoIcon /> },
        { route: PagesList.discography.route, name: 'Discography', icon: <AlbumIcon /> },
        // { route: PagesList.newsletter.route, name: 'Newsletter', icon: <NewspaperIcon /> },
        { route: PagesList.photos.route, name: 'Photos', icon: <CropOriginalIcon /> },
        { route: PagesList.videos.route, name: 'Videos', icon: <TheatersIcon /> },
        { route: PagesList.events.route, url: null, name: 'Events', icon: <RocketLaunchIcon /> },
        { route: PagesList.share.route, url: null, name: 'Share it', icon: <FileUploadIcon /> },
        // { route: null, url: paypalDonateLink, name: 'Donate', icon: <ArchiveIcon /> },
    ];

    return (
        <>
            {isColumnMenu === true ? (
                menuOptions.map((menuOption, i) => (
                    <MenuItem
                        onClick={handleClose}
                        disableRipple
                        key={i}
                        sx={{
                            padding: '12px 16px', // Increased padding for larger touch targets
                            minHeight: '48px', // Ensure minimum height
                        }}
                    >
                        {menuOption.route === null ? (
                            <StyledAnchor id={`${i}`} href={menuOption.url} target="_blank" rel="noreferrer">
                                <IconButton
                                    size="large" // Changed to large
                                    sx={{
                                        color: theme.palette.text.primary,
                                        padding: '8px', // Added padding
                                    }}
                                >
                                    {React.cloneElement(menuOption.icon, {
                                        fontSize: 'large', // Changed to large
                                        sx: { fontSize: '28px' } // Explicit size
                                    })}
                                </IconButton>
                                <Typography sx={commonTypographyStyle}>{menuOption.name}</Typography>
                            </StyledAnchor>
                        ) : (
                            <IconButton
                                id={i}
                                href={menuOption.route}
                                size="large" // Changed to large
                                sx={{
                                    color: theme.palette.text.primary,
                                    padding: '8px 12px', // Added padding
                                    gap: '6px', // Space between icon and text
                                }}
                            >
                                {React.cloneElement(menuOption.icon, {
                                    fontSize: 'large',
                                    sx: { fontSize: '28px' }
                                })}
                                <Typography sx={commonTypographyStyle}>{menuOption.name}</Typography>
                            </IconButton>
                        )}
                    </MenuItem>
                ))
            ) : (
                menuOptions.map((menuOption, i) => (
                    menuOption.route === null ? (
                        <StyledAnchor
                            id={`${i}`}
                            href={menuOption.url}
                            target="_blank"
                            rel="noreferrer"
                            key={i}
                            sx={{
                                padding: '8px 14px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    borderRadius: '4px',
                                }
                            }}
                        >
                            <Typography sx={commonTypographyStyle}>{menuOption.name}</Typography>
                        </StyledAnchor>
                    ) : (
                        <IconButton
                            id={i}
                            href={menuOption.route}
                            key={i}
                            size="large" // Changed to large
                            sx={{
                                color: theme.palette.text.primary,
                                padding: '8px 14px', // Added padding
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                    borderRadius: '4px',
                                }
                            }}
                        >
                            <Typography sx={commonTypographyStyle}>{menuOption.name}</Typography>
                        </IconButton>
                    )
                ))
            )}
        </>
    );
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    width: '100%',
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: 0,
    marginRight: 0,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.up("sm")]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
        paddingLeft: 0,
        paddingRight: 0,
    },
    opacity: 1,
    visibility: 'visible',
    transition: "opacity 0.3s, visibility 0.3s",
    '&.hidden': {
        opacity: 0,
        visibility: 'hidden',
        pointerEvents: 'none',
    },
}));

export default function NavBar() {
    const { isNavBarVisible, isMobile, isTablet } = useContext(LayoutContext);
    const { theme } = useContext(ThemeContext);

    return (
        <React.Fragment>
            <StyledAppBar component="nav" position="fixed" className={isNavBarVisible ? '' : 'hidden'}>
                <Grid container direction="row" justifyContent="center" alignItems="center" sx={{ height: 70, width: '100vw' }}>
                    <Grid item lg={5} sm={9} xs={9} sx={{ display: "flex", justifyContent: 'center' }}>
                        <Typography component={Link} to={PagesList.homepage.route} variant={isMobile ? 'h6' : 'h5'} fontFamily={theme.typography.fontFamily}>
                            Camping In The Skies
                        </Typography>
                    </Grid>
                    <Grid item lg={7} sm={3} xs={3} sx={{ display: "flex", gap: isMobile ? "0" : "10px", justifyContent: 'center' }}>
                        {(isMobile || isTablet) ? <MenuNavBar /> : <MainMenuOptions />}
                    </Grid>
                </Grid>
            </StyledAppBar>
        </React.Fragment>
    );
}